import { Calendar, ChatBubble, Database, EditPencil, HeadsetWarning, Page, Phone, SparksSolid } from 'iconoir-react'
import { ReactElement } from 'react'
import { ChannelCheckbox, ChannelHeaderRow, ChannelHeading, NotificationRow } from './ChannelColumns'
import { LiveAlertsSection } from './LiveAlertsSection'
import { NotificationSectionCard } from './NotificationSectionCard'
import { Product } from 'app/components/auth/Product'
import Spinner from 'app/components/Spinner'
import FloatingSave from 'app/components/FloatingSave'
import { SmartForm } from 'app/components/form/SmartForm'
import { Switch } from 'app/components/ui/switch'
import { editValidator } from './notificationValidator'
import type { SectionIcon } from 'app/types/live-alerts'
import type { UseUserNotificationsReturn, NotificationSettings } from './useUserNotifications'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validator = editValidator as any

export type UserNotificationsShadcnComponentProps = UseUserNotificationsReturn

function LineItem({
  title,
  description,
  name,
  defaultCheckedEmail = false,
  defaultCheckedApp = false,
  disabled = false,
  emailOnly = false,
}: {
  title: string
  description: string
  name: string
  defaultCheckedEmail?: boolean
  defaultCheckedApp?: boolean
  disabled?: boolean
  emailOnly?: boolean
}): ReactElement {
  const email = (
    <ChannelCheckbox
      label="Email"
      title={title}
      name={`${name}.email`}
      defaultChecked={defaultCheckedEmail}
      disabled={disabled}
    />
  )
  return (
    <NotificationRow title={title} description={description}>
      {emailOnly ? (
        email
      ) : (
        <>
          {email}
          <ChannelCheckbox
            label="In-app"
            title={title}
            name={`${name}.app`}
            defaultChecked={defaultCheckedApp}
            disabled={disabled}
          />
        </>
      )}
    </NotificationRow>
  )
}

type SectionConfig = {
  title: string
  description: string
  icon: SectionIcon
  sectionKey: keyof NotificationSettings | 'aiCallDiagnostics' | 'aiAgentChanges'
  // The section's stored values when they do not come from the public notification
  // settings: Agent Diagnostics loads from the ops schema.
  data?: Record<string, any>
  enabled: boolean
  onToggle: (val: boolean) => void
  items: { title: string; description: string; itemKey: string }[]
  // Email is the only channel in the email-only (org) view.
  emailOnly?: boolean
}

function NotificationSection({
  config,
  notificationSettings,
  setIsDirtyForm,
}: {
  config: SectionConfig
  notificationSettings: NotificationSettings | undefined
  setIsDirtyForm: (v: boolean) => void
}) {
  const sectionData = (config.data ?? notificationSettings?.[config.sectionKey as keyof NotificationSettings]) as
    | Record<string, any>
    | undefined
  return (
    <NotificationSectionCard
      icon={config.icon}
      title={config.title}
      description={config.description}
      enabled={config.enabled}
      control={
        <Switch
          aria-label={config.title}
          name={`${config.sectionKey}.enabled`}
          defaultChecked={sectionData?.enabled}
          value="on"
          onCheckedChange={(val) => {
            config.onToggle(val)
            setIsDirtyForm(true)
          }}
        />
      }
    >
      <ChannelHeaderRow>
        {config.emailOnly ? (
          <ChannelHeading label="Email" />
        ) : (
          <>
            <ChannelHeading label="Email" />
            <ChannelHeading label="In-app" />
          </>
        )}
      </ChannelHeaderRow>
      {config.items.map((item) => (
        <LineItem
          key={item.itemKey}
          title={item.title}
          description={item.description}
          name={`${config.sectionKey}.${item.itemKey}`}
          defaultCheckedEmail={sectionData?.[item.itemKey]?.email}
          defaultCheckedApp={sectionData?.[item.itemKey]?.app}
          disabled={!config.enabled}
          emailOnly={config.emailOnly}
        />
      ))}
    </NotificationSectionCard>
  )
}

export function UserNotificationsShadcnComponent(props: UserNotificationsShadcnComponentProps): ReactElement {
  const {
    notificationSettings,
    loading,
    canEditAgentDiagnostics,
    agentDiagnostics,
    alertEmail,
    isDirtyForm,
    setIsDirtyForm,
    phoneCallSectionEnabled,
    setPhoneCallSectionEnabled,
    AIAgentSectionEnabled,
    setAIAgentSectionEnabled,
    textMessageSectionEnabled,
    setTextMessageSectionEnabled,
    formSubmissionSectionEnabled,
    setFormSubmissionSectionEnabled,
    schedulerSectionEnabled,
    setSchedulerSectionEnabled,
    pmsDataSectionEnabled,
    setPmsDataSectionEnabled,
    aiCallDiagnosticsSectionEnabled,
    setAiCallDiagnosticsSectionEnabled,
    agentChangesSectionEnabled,
    setAgentChangesSectionEnabled,
    liveAlerts,
    setLiveAlerts,
    formRef,
    onSave,
  } = props

  const phoneCallsConfig: SectionConfig = {
    title: 'Phone Calls',
    description: 'Includes all the notifications related to call activities that need your attention.',
    icon: Phone,
    sectionKey: 'phoneCalls',
    enabled: phoneCallSectionEnabled,
    onToggle: setPhoneCallSectionEnabled,
    items: [
      {
        title: 'New Patient Unscheduled Calls',
        description: 'When the caller is a new patient and the call ends without a scheduled appointment',
        itemKey: 'newPatientUnscheduledCalls',
      },
      {
        title: 'Callback Requested',
        description: 'The caller says they will call back, or when the caller asks to be called back',
        itemKey: 'callbackRequested',
      },
      {
        title: 'Missed Calls',
        description:
          "When the call does not connect with the office or when they reach the voicemail and don't leave a message",
        itemKey: 'missedCalls',
      },
      {
        title: 'Voicemail',
        description: 'The call goes to voicemail and the caller leaves a message',
        itemKey: 'voicemail',
      },
      {
        title: 'Canceled Appointment',
        description: "A scheduled appointment got canceled and a new appointment wasn't rescheduled",
        itemKey: 'canceledAppointment',
      },
      {
        title: 'All Unscheduled Calls',
        description:
          'When the caller is interested in booking a dental appointment but the call ends without a scheduled appointment',
        itemKey: 'allUnscheduledCalls',
      },
    ],
  }

  const aiAgentConfig: SectionConfig = {
    title: 'AI Agent',
    description: 'Includes all the notifications related to AI Agents.',
    icon: SparksSolid,
    sectionKey: 'aiAgent',
    enabled: AIAgentSectionEnabled,
    onToggle: setAIAgentSectionEnabled,
    items: [
      { title: 'New AI Agent Call', description: 'Any time the AI Agent receives a call', itemKey: 'newAIAgentCall' },
      {
        title: 'New Appointment Booked',
        description: 'Any time the AI Agent books a new appointment',
        itemKey: 'newAppointmentBooked',
      },
      {
        title: 'Emergency Appointment Request',
        description: 'When the AI cannot book an emergency appointment',
        itemKey: 'emergencyAppointmentRequest',
      },
    ],
  }

  const textMessagesConfig: SectionConfig = {
    title: 'Text Messages',
    description: 'Includes all the notifications related to the SMS inbox.',
    icon: ChatBubble,
    sectionKey: 'textMessages',
    enabled: textMessageSectionEnabled,
    onToggle: setTextMessageSectionEnabled,
    items: [
      {
        title: 'New Text Message',
        description: 'Any time Trackable receives a new text message',
        itemKey: 'newTextMessage',
      },
    ],
  }

  const formSubmissionsConfig: SectionConfig = {
    title: 'Form Submissions',
    description: 'Includes all the notifications related to form submissions.',
    icon: Page,
    sectionKey: 'formSubmissions',
    enabled: formSubmissionSectionEnabled,
    onToggle: setFormSubmissionSectionEnabled,
    items: [
      {
        title: 'New Form Submission',
        description: 'Any time Trackable receives a new form submission',
        itemKey: 'newFormSubmission',
      },
    ],
  }

  const schedulerConfig: SectionConfig = {
    title: 'Trackable Online Scheduler Submissions',
    description: 'Includes all the notifications related to the Trackable online scheduler.',
    icon: Calendar,
    sectionKey: 'scheduler',
    enabled: schedulerSectionEnabled,
    onToggle: setSchedulerSectionEnabled,
    items: [
      {
        title: 'New Appointment Booked',
        description: 'Any time a new appointment is booked from the Trackable online scheduler',
        itemKey: 'newAppointmentBooked',
      },
    ],
  }

  const pmsDataConfig: SectionConfig = {
    title: 'PMS Data',
    description: 'Includes all the notifications that relate to the data Trackable recieves from your PMS.',
    icon: Database,
    sectionKey: 'pmsData',
    enabled: pmsDataSectionEnabled,
    onToggle: setPmsDataSectionEnabled,
    items: [
      {
        title: 'No Patient Linked on Scheduled Contact',
        description: 'When a scheduled activity is received but Trackable cannot find an appropriate patient match',
        itemKey: 'noPatientLinkedOnScheduledContact',
      },
      {
        title: 'No Appointment Found on Scheduled Contact',
        description:
          'When a scheduled activity is received and Trackable links to the patient but no appointment is found',
        itemKey: 'noAppointmentFoundOnScheduledContact',
      },
      {
        title: 'New Patient Broken Appointment',
        description: "When a new patient's scheduled appointment time has passed but no completed appointment is found",
        itemKey: 'brokenAppointment',
      },
    ],
  }

  const aiCallDiagnosticsConfig: SectionConfig = {
    title: 'Agent Diagnostics',
    description: `Emails ${alertEmail ?? 'you'} when an AI agent's tool fails on a call, in any org. One email per call.`,
    icon: HeadsetWarning,
    sectionKey: 'aiCallDiagnostics',
    data: {
      enabled: agentDiagnostics?.enabled,
      toolError: { email: agentDiagnostics?.toolError },
      toolTimeout: { email: agentDiagnostics?.toolTimeout },
      toolDuplicate: { email: agentDiagnostics?.toolDuplicate },
      noCallsIn48h: { email: agentDiagnostics?.noCallsIn48h },
    },
    enabled: aiCallDiagnosticsSectionEnabled,
    onToggle: setAiCallDiagnosticsSectionEnabled,
    emailOnly: true,
    items: [
      { title: 'Tool Error', description: 'A tool the agent called returned an error', itemKey: 'toolError' },
      { title: 'Tool Timeout', description: 'A tool the agent called did not answer in time', itemKey: 'toolTimeout' },
      {
        title: 'Tool Duplicate',
        description: 'The agent called the same tool again with the same request',
        itemKey: 'toolDuplicate',
      },
      {
        title: 'No calls in 48h',
        description: 'An AI agent location has had no AI call for two or more days',
        itemKey: 'noCallsIn48h',
      },
    ],
  }

  const agentChangesConfig: SectionConfig = {
    title: 'Agent Changes',
    description: `Emails ${alertEmail ?? 'you'} when an AI agent's settings change, in any org.`,
    icon: EditPencil,
    sectionKey: 'aiAgentChanges',
    data: {
      enabled: agentDiagnostics?.agentChangesEnabled,
      offers: { email: agentDiagnostics?.agentChangeOffers },
      services: { email: agentDiagnostics?.agentChangeServices },
      businessDetails: { email: agentDiagnostics?.agentChangeBusinessDetails },
      appointmentTypes: { email: agentDiagnostics?.agentChangeAppointmentTypes },
    },
    enabled: agentChangesSectionEnabled,
    onToggle: setAgentChangesSectionEnabled,
    emailOnly: true,
    items: [
      { title: 'Offers', description: 'The offers the agent can mention to callers', itemKey: 'offers' },
      {
        title: 'Services',
        description: 'The services the agent lists, their prices and consults',
        itemKey: 'services',
      },
      {
        title: 'Business Details',
        description:
          "The agent's name and voice, the business name and address, the greeting and the post-booking lines",
        itemKey: 'businessDetails',
      },
      {
        title: 'Appointment Types',
        description: 'The appointment types the agent books, and the booking confirmation text being on or off',
        itemKey: 'appointmentTypes',
      },
    ],
  }

  const liveAlertsSection = <LiveAlertsSection liveAlerts={liveAlerts} setLiveAlerts={setLiveAlerts} editable />

  if (loading) {
    return (
      <div className="flex w-full justify-center py-20">
        <Spinner size={8} />
      </div>
    )
  }

  return (
    <SmartForm
      id="notifications-form"
      className="relative !mt-0 max-w-6xl"
      validator={validator}
      reloadDocument={false}
      hideButtons
      formRef={formRef}
      onChange={() => setIsDirtyForm(true)}
    >
      {canEditAgentDiagnostics ? (
        // Super admins are org-level, so the workspace sections below have nothing
        // to store for them.
        <div className="col-span-6 flex w-full flex-col gap-6 pb-24">
          <NotificationSection
            config={aiCallDiagnosticsConfig}
            notificationSettings={notificationSettings}
            setIsDirtyForm={setIsDirtyForm}
          />
          <NotificationSection
            config={agentChangesConfig}
            notificationSettings={notificationSettings}
            setIsDirtyForm={setIsDirtyForm}
          />
          {liveAlertsSection}
        </div>
      ) : (
        <div className="col-span-6 flex w-full flex-col gap-6 pb-24">
          {liveAlertsSection}
          {/* AI Agent notifications belong to the AI Agent product, not CRM. Keeping this
              inside the CRM gate hid the whole section from AI-agent-only clients (TRK-5896). */}
          <Product type="AI Agent">
            <NotificationSection
              config={aiAgentConfig}
              notificationSettings={notificationSettings}
              setIsDirtyForm={setIsDirtyForm}
            />
          </Product>
          <Product type="CRM">
            <>
              <NotificationSection
                config={phoneCallsConfig}
                notificationSettings={notificationSettings}
                setIsDirtyForm={setIsDirtyForm}
              />
              <Product type="Inbox">
                <NotificationSection
                  config={textMessagesConfig}
                  notificationSettings={notificationSettings}
                  setIsDirtyForm={setIsDirtyForm}
                />
              </Product>
              <NotificationSection
                config={formSubmissionsConfig}
                notificationSettings={notificationSettings}
                setIsDirtyForm={setIsDirtyForm}
              />
            </>
          </Product>
          <Product type="Scheduler">
            <NotificationSection
              config={schedulerConfig}
              notificationSettings={notificationSettings}
              setIsDirtyForm={setIsDirtyForm}
            />
          </Product>
          <Product type="CRM">
            <NotificationSection
              config={pmsDataConfig}
              notificationSettings={notificationSettings}
              setIsDirtyForm={setIsDirtyForm}
            />
          </Product>
        </div>
      )}
      <FloatingSave isDirtyForm={isDirtyForm} handleSaveConfiguration={onSave} />
    </SmartForm>
  )
}
